import Cookie from 'js-cookie';

interface SecureCookieOptions {
  expires?: number | Date;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

// Default secure cookie options
const DEFAULT_SECURE_OPTIONS: SecureCookieOptions = {
  secure: true, // Only send over HTTPS
  sameSite: 'lax', // CSRF protection while allowing some cross-site requests
  path: '/', // Available across the entire site
};

// For sensitive cookies that need stricter security
const STRICT_SECURE_OPTIONS: SecureCookieOptions = {
  secure: true,
  sameSite: 'strict', // Strict CSRF protection
  path: '/',
};

// For cookies that need to work cross-site (like analytics)
const CROSS_SITE_SECURE_OPTIONS: SecureCookieOptions = {
  secure: true,
  sameSite: 'none', // Allow cross-site but require secure flag
  path: '/',
};

/**
 * Set a secure cookie with default security settings
 */
export const setSecureCookie = (name: string, value: string, options: SecureCookieOptions = {}): void => {
  const mergedOptions = { ...DEFAULT_SECURE_OPTIONS, ...options };
  Cookie.set(name, value, mergedOptions);
};

/**
 * Set a strict secure cookie for sensitive data
 */
export const setStrictSecureCookie = (name: string, value: string, options: SecureCookieOptions = {}): void => {
  const mergedOptions = { ...STRICT_SECURE_OPTIONS, ...options };
  Cookie.set(name, value, mergedOptions);
};

/**
 * Set a cross-site secure cookie for analytics/tracking
 */
export const setCrossSiteSecureCookie = (name: string, value: string, options: SecureCookieOptions = {}): void => {
  const mergedOptions = { ...CROSS_SITE_SECURE_OPTIONS, ...options };
  Cookie.set(name, value, mergedOptions);
};

/**
 * Get cookie value (same as js-cookie get)
 */
export const getCookie = (name: string): string | undefined => {
  return Cookie.get(name);
};

/**
 * Remove cookie with secure settings
 */
export const removeSecureCookie = (name: string, options: SecureCookieOptions = {}): void => {
  const mergedOptions = { ...DEFAULT_SECURE_OPTIONS, ...options };
  Cookie.remove(name, mergedOptions);
};

/**
 * Check if we're in a secure context (HTTPS or localhost)
 */
export const isSecureContext = (): boolean => {
  return (
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
};

/**
 * Get domain for cookie setting
 */
export const getCookieDomain = (): string => {
  const hostname = window.location.hostname;
  try {
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
    return hostname;
  } catch {
    return hostname;
  }
};
